import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../../api/client';

const inputClass = 'w-full px-3.5 py-2.5 rounded-lg border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-shadow';
const labelClass = 'block text-sm font-medium mb-1.5 text-foreground';

// Matches the profession values used elsewhere in the admin B2B UI.
const PROFESSION_OPTIONS = [
  ['apotheker', 'Apotheker'],
  ['dokter', 'Dokter'],
  ['therapeut', 'Therapeut'],
  ['kinesist', 'Kinesist'],
  ['verpleegkundige', 'Verpleegkundige'],
  ['anders', 'Anders'],
];

const SOURCE_OPTIONS = ['admin', 'herbosoft'];

const blankForm = () => ({
  // Identity
  firstName: '', lastName: '', email: '', phone: '',
  // Business
  companyName: '', profession: '', professionOther: '', vatNumber: '',
  // Canonical address (short names — backend aliases them to b2b_orgs)
  street: '', houseNumber: '', box: '',
  postalCode: '', city: '', country: 'BE',
  // Internal / migration
  commissionRate: '15', discountRate: '',
  source: 'admin', importedFrom: '', legacyReference: '', externalReference: '',
  notes: '', isApproved: true,
});

// Populate form from server docs (person + linked org).
const hydrate = (person) => {
  const org = person?.org || {};
  return {
    firstName: person?.firstName || '',
    lastName: person?.lastName || '',
    email: person?.email || '',
    phone: person?.phone || '',
    companyName: org?.companyName || '',
    profession: person?.profession || '',
    professionOther: person?.professionOther || '',
    vatNumber: org?.vatNumber || '',
    // Legacy long names on b2b_orgs ↔ short names on the form.
    street: org?.addressStreet || '',
    houseNumber: org?.addressNumber || '',
    box: org?.addressBus || '',
    postalCode: org?.addressPostalCode || '',
    city: org?.addressCity || '',
    country: org?.addressCountry || 'BE',
    commissionRate: person?.commissionRate != null ? String(person.commissionRate) : '15',
    discountRate: person?.discountRate != null ? String(person.discountRate) : '',
    source: person?.source || 'admin',
    importedFrom: person?.importedFrom || '',
    legacyReference: person?.legacyReference || '',
    externalReference: person?.externalReference || '',
    notes: person?.notes || '',
    isApproved: person?.isApproved !== false,
  };
};

// Trim strings, convert numeric rates, drop empty values so the backend
// doesn't overwrite unmanaged fields.
const toPayload = (f, { isEdit }) => {
  const rates = {
    commissionRate: f.commissionRate !== '' ? parseFloat(f.commissionRate) : undefined,
    discountRate: f.discountRate === '' ? null : parseFloat(f.discountRate),
  };
  const body = {
    firstName: f.firstName.trim(),
    lastName: f.lastName.trim(),
    email: f.email.trim().toLowerCase() || null,
    phone: f.phone.trim(),
    companyName: f.companyName.trim(),
    profession: f.profession || '',
    professionOther: f.profession === 'anders' ? f.professionOther.trim() : '',
    vatNumber: f.vatNumber.trim(),
    // Short-name address fields — backend aliases to b2b_orgs canonical keys.
    street: f.street.trim(),
    houseNumber: f.houseNumber.trim(),
    box: f.box.trim(),
    postalCode: f.postalCode.trim(),
    city: f.city.trim(),
    country: (f.country || 'BE').trim(),
    source: f.source || 'admin',
    importedFrom: f.importedFrom.trim(),
    legacyReference: f.legacyReference.trim(),
    externalReference: f.externalReference.trim(),
    notes: f.notes,
    isApproved: !!f.isApproved,
    ...rates,
  };
  // For create, drop truly empty optional strings so create validation is clean.
  if (!isEdit) {
    Object.keys(body).forEach(k => { if (body[k] === '' || body[k] == null) delete body[k]; });
    // Rates fall through: commissionRate NaN → drop
    if (Number.isNaN(body.commissionRate)) delete body.commissionRate;
  }
  return body;
};

export default function PrescriberForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [form, setForm] = useState(blankForm);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Migration provenance: source + legacyReference are immutable once
  // set. Editing them on existing records causes silent drift in the
  // historical paper trail (e.g. an admin accidentally re-typing
  // "admin" over a "herbosoft" record breaks reporting). Lock them in
  // edit mode whenever they were already populated server-side.
  const [lockedFields, setLockedFields] = useState({ source: false, legacyReference: false });

  useEffect(() => {
    if (!isEdit) return;
    adminApi.getB2B(id).then(r => {
      const person = r.data || {};
      setForm(hydrate(person));
      setLockedFields({
        source: !!(person.source && person.source !== 'admin'),
        legacyReference: !!person.legacyReference,
      });
    }).finally(() => setLoading(false));
  }, [id, isEdit]);

  const on = (k) => (e) => setForm(s => ({ ...s, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      if (isEdit) {
        await adminApi.updatePrescriberProfile(id, toPayload(form, { isEdit: true }));
        toast.success('Voorschrijver bijgewerkt');
        navigate(`/admin/b2b/${id}`);
      } else {
        const { data } = await adminApi.createPrescriber(toPayload(form, { isEdit: false }));
        toast.success('Voorschrijver aangemaakt');
        navigate(`/admin/b2b/${data.person._id}`);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8"><div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin" /></div>;

  return (
    <div data-testid="prescriber-form-page" className="p-8 max-w-3xl">
      <Link to={isEdit ? `/admin/b2b/${id}` : '/admin/b2b'} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-6 font-medium">
        <ArrowLeft className="w-4 h-4" /> Terug
      </Link>

      <h1 className="font-serif text-3xl font-light text-foreground mb-2">
        {isEdit ? 'Voorschrijver bewerken' : 'Nieuwe voorschrijver'}
      </h1>
      <p className="text-sm text-muted-foreground mb-8">
        Adres &amp; BTW-nummer worden canoniek opgeslagen op de organisatie (<code>b2b_orgs</code>).
      </p>

      {error && (
        <div data-testid="prescriber-form-error" className="mb-6 px-4 py-3 rounded-lg text-sm bg-destructive/10 text-destructive border border-destructive/20">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Identity */}
        <section className="bg-card border border-border rounded-2xl p-6 shadow-sm">
          <h2 className="font-serif text-base font-medium mb-4">Identiteit</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Voornaam</label>
              <input data-testid="pf-firstName" className={inputClass} value={form.firstName} onChange={on('firstName')} />
            </div>
            <div>
              <label className={labelClass}>Achternaam</label>
              <input data-testid="pf-lastName" className={inputClass} value={form.lastName} onChange={on('lastName')} />
            </div>
            <div>
              <label className={labelClass}>E-mail</label>
              <input data-testid="pf-email" type="email" className={inputClass} value={form.email} onChange={on('email')} />
            </div>
            <div>
              <label className={labelClass}>Telefoon</label>
              <input data-testid="pf-phone" className={inputClass} value={form.phone} onChange={on('phone')} />
            </div>
          </div>
        </section>

        {/* Business (on org) */}
        <section className="bg-card border border-border rounded-2xl p-6 shadow-sm">
          <h2 className="font-serif text-base font-medium mb-4">Bedrijf &amp; BTW <span className="text-xs text-muted-foreground font-normal">(op b2b_orgs)</span></h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className={labelClass}>Bedrijfsnaam</label>
              <input data-testid="pf-companyName" className={inputClass} value={form.companyName} onChange={on('companyName')} />
            </div>
            <div>
              <label className={labelClass}>Beroep / type</label>
              <select data-testid="pf-profession" className={inputClass} value={form.profession} onChange={on('profession')}>
                <option value="">— kies —</option>
                {PROFESSION_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>BTW-nummer (BE…)</label>
              <input data-testid="pf-vatNumber" className={inputClass} value={form.vatNumber} onChange={on('vatNumber')} placeholder="BE0123456789" />
            </div>
            {form.profession === 'anders' && (
              <div className="col-span-2">
                <label className={labelClass}>Beroep (andere)</label>
                <input data-testid="pf-professionOther" className={inputClass} value={form.professionOther} onChange={on('professionOther')} />
              </div>
            )}
          </div>
        </section>

        {/* Address (on org) */}
        <section className="bg-card border border-border rounded-2xl p-6 shadow-sm">
          <h2 className="font-serif text-base font-medium mb-4">Adres <span className="text-xs text-muted-foreground font-normal">(op b2b_orgs)</span></h2>
          <div className="grid grid-cols-6 gap-4">
            <div className="col-span-3">
              <label className={labelClass}>Straat</label>
              <input data-testid="pf-street" className={inputClass} value={form.street} onChange={on('street')} />
            </div>
            <div className="col-span-2">
              <label className={labelClass}>Huisnummer</label>
              <input data-testid="pf-houseNumber" className={inputClass} value={form.houseNumber} onChange={on('houseNumber')} />
            </div>
            <div>
              <label className={labelClass}>Bus</label>
              <input data-testid="pf-box" className={inputClass} value={form.box} onChange={on('box')} />
            </div>
            <div className="col-span-2">
              <label className={labelClass}>Postcode</label>
              <input data-testid="pf-postalCode" className={inputClass} value={form.postalCode} onChange={on('postalCode')} />
            </div>
            <div className="col-span-3">
              <label className={labelClass}>Gemeente</label>
              <input data-testid="pf-city" className={inputClass} value={form.city} onChange={on('city')} />
            </div>
            <div>
              <label className={labelClass}>Land</label>
              <input data-testid="pf-country" className={inputClass} value={form.country} onChange={on('country')} maxLength={2} />
            </div>
          </div>
        </section>

        {/* Internal / migration */}
        <section className="bg-card border border-border rounded-2xl p-6 shadow-sm">
          <h2 className="font-serif text-base font-medium mb-4">Commissie, korting &amp; herkomst</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Commissie (%)</label>
              <input data-testid="pf-commissionRate" type="number" step="0.1" className={inputClass} value={form.commissionRate} onChange={on('commissionRate')} />
            </div>
            <div>
              <label className={labelClass}>Individuele korting (%)</label>
              <input data-testid="pf-discountRate" type="number" step="0.1" min="0" max="100" placeholder="leeg = geen korting" className={inputClass} value={form.discountRate} onChange={on('discountRate')} />
            </div>
            <div>
              <label className={labelClass}>Bron</label>
              {lockedFields.source ? (
                <>
                  <input
                    data-testid="pf-source"
                    className={`${inputClass} bg-muted/40 cursor-not-allowed text-muted-foreground`}
                    value={form.source}
                    readOnly
                    aria-readonly="true"
                  />
                  <p className="text-xs text-muted-foreground mt-1 italic">Migratie-veld — kan niet gewijzigd worden.</p>
                </>
              ) : (
                <select data-testid="pf-source" className={inputClass} value={form.source} onChange={on('source')}>
                  {SOURCE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              )}
            </div>
            <div>
              <label className={labelClass}>Geïmporteerd uit</label>
              <input data-testid="pf-importedFrom" className={inputClass} value={form.importedFrom} onChange={on('importedFrom')} placeholder="bv. HerboSoft" />
            </div>
            <div>
              <label className={labelClass}>Legacy referentie</label>
              <input
                data-testid="pf-legacyReference"
                className={lockedFields.legacyReference ? `${inputClass} bg-muted/40 cursor-not-allowed text-muted-foreground` : inputClass}
                value={form.legacyReference}
                onChange={on('legacyReference')}
                readOnly={lockedFields.legacyReference}
                aria-readonly={lockedFields.legacyReference || undefined}
              />
              {lockedFields.legacyReference && (
                <p className="text-xs text-muted-foreground mt-1 italic">Migratie-veld — kan niet gewijzigd worden.</p>
              )}
            </div>
            <div>
              <label className={labelClass}>Externe referentie</label>
              <input data-testid="pf-externalReference" className={inputClass} value={form.externalReference} onChange={on('externalReference')} />
            </div>
            <div className="col-span-2">
              <label className={labelClass}>Notities</label>
              <textarea data-testid="pf-notes" className={`${inputClass} min-h-[80px]`} value={form.notes} onChange={on('notes')} />
            </div>
            <label className="col-span-2 inline-flex items-center gap-2 text-sm mt-2">
              <input data-testid="pf-isApproved" type="checkbox" checked={form.isApproved} onChange={e => setForm(s => ({ ...s, isApproved: e.target.checked }))} />
              Goedgekeurd
            </label>
          </div>
        </section>

        <div className="flex items-center gap-3">
          <button
            data-testid="pf-submit"
            type="submit" disabled={saving}
            className="bg-primary text-primary-foreground rounded-full px-8 py-3 text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 transition-all active:scale-95 shadow-sm"
          >
            {saving ? 'Bezig...' : (isEdit ? 'Wijzigingen opslaan' : 'Voorschrijver aanmaken')}
          </button>
          <Link
            to={isEdit ? `/admin/b2b/${id}` : '/admin/b2b'}
            className="text-sm text-muted-foreground hover:text-primary font-medium"
          >
            Annuleren
          </Link>
        </div>
      </form>
    </div>
  );
}
